import { useState } from 'react';
import { apiClient, ApiError } from '@/lib/api/client';
import { useDialogFocus } from '@/lib/useDialogFocus';
import { PROJECT_COLORS } from '@/lib/projectColors';
import { cn } from '@/lib/utils';

interface Props {
  onCreated: (project_id: string) => void;
  onClose: () => void;
}

export function NewProjectModal({ onCreated, onClose }: Props) {
  const [name, setName] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const formRef = useDialogFocus<HTMLFormElement>();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await apiClient.createProject(name.trim(), color ? { color } : {});
      onCreated(r.project_id);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to create project');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-project-title"
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-4"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <form
        ref={formRef}
        onSubmit={submit}
        className="w-full max-w-md space-y-5 rounded-2xl border border-border bg-card p-7 shadow-2xl"
      >
        <div className="space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            New project
          </div>
          <h2 id="new-project-title" className="text-2xl font-semibold tracking-tight">Create a project</h2>
          <p className="text-sm text-muted-foreground">
            You'll be the owner. Invite collaborators after.
          </p>
        </div>
        <div className="space-y-2">
          <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
            maxLength={64}
            placeholder="Q4 Launch"
            className="block w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30"
          />
        </div>
        <div className="space-y-1.5">
          <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Accent color · optional
          </label>
          <div role="radiogroup" aria-label="Accent color" className="flex flex-wrap items-center gap-2">
            {PROJECT_COLORS.map((c) => {
              const selected = color === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={c.label}
                  title={c.label}
                  onClick={() => setColor(selected ? null : c.id)}
                  className={cn(
                    'no-glow size-6 rounded-full transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-card',
                    selected
                      ? 'ring-2 ring-offset-2 ring-offset-card scale-110'
                      : 'opacity-80 hover:opacity-100 hover:scale-105',
                  )}
                  style={{
                    backgroundColor: c.oklch,
                    ...(selected ? { boxShadow: `0 0 16px -2px ${c.oklch}` } : {}),
                  }}
                />
              );
            })}
            {(() => {
              const isCustomColor =
                color !== null && !PROJECT_COLORS.some((c) => c.id === color);
              return (
                <label
                  className={cn(
                    'no-glow relative size-6 cursor-pointer rounded-full transition-all duration-150',
                    isCustomColor
                      ? 'ring-2 ring-offset-2 ring-offset-card scale-110'
                      : 'opacity-80 hover:opacity-100 hover:scale-105',
                  )}
                  title="Custom color"
                  style={{
                    background: isCustomColor
                      ? color!
                      : 'conic-gradient(from 0deg, oklch(0.7 0.2 0), oklch(0.78 0.18 60), oklch(0.75 0.16 130), oklch(0.7 0.14 200), oklch(0.65 0.2 280), oklch(0.7 0.2 0))',
                    ...(isCustomColor ? { boxShadow: `0 0 16px -2px ${color}` } : {}),
                  }}
                >
                  <input
                    type="color"
                    className="no-glow absolute inset-0 size-full cursor-pointer opacity-0"
                    value={isCustomColor ? color! : '#a3c2ff'}
                    onChange={(e) => setColor(e.target.value)}
                    aria-label="Pick custom color"
                  />
                </label>
              );
            })()}
            {color && (
              <button
                type="button"
                onClick={() => setColor(null)}
                className="ml-1 rounded px-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
                title="Clear color"
              >
                clear
              </button>
            )}
          </div>
        </div>
        {err && (
          <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
            {err}
          </div>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-md border border-border bg-background px-4 py-2.5 text-sm text-muted-foreground hover:border-ring/60 hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || busy}
            className="flex-1 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}
